import { RECORDINGS } from '@listen-draw/shared';

let context: AudioContext | undefined;
const buffers = new Map<string, Promise<AudioBuffer>>();
const MAX_CACHED_RECORDINGS = 48;
export const SOUND_CHECK = 'Sound is on. You are ready to play.';

export function audioContext(): AudioContext {
  if (!context || context.state === 'closed') context = new AudioContext();
  return context;
}

/** Call directly from a tap/click so Safari and mobile browsers can enable sound. */
export async function unlockAudio(): Promise<void> {
  const ctx = audioContext();
  if (ctx.state !== 'running') await ctx.resume();
  if (ctx.state !== 'running') throw new Error('audio_locked');
}

export function recordingDuration(text: string, rate = 1): number | undefined {
  const info = RECORDINGS[text];
  return info ? info.durationMs / rate : undefined;
}

async function bufferFor(text: string): Promise<AudioBuffer> {
  const info = RECORDINGS[text];
  if (!info) throw new Error('recording_unavailable');
  const url = `/audio/${info.bank ?? 'en-v1'}/${info.file}`;
  const cached = buffers.get(url);
  if (cached) {
    buffers.delete(url);
    buffers.set(url, cached);
    return cached;
  }
  const pending = (async () => {
    const response = await fetch(url, {
      signal: AbortSignal.timeout(15000),
    });
    if (!response.ok || !response.headers.get('content-type')?.startsWith('audio/'))
      throw new Error('audio_download_failed');
    return audioContext().decodeAudioData(await response.arrayBuffer());
  })();
  buffers.set(url, pending);
  while (buffers.size > MAX_CACHED_RECORDINGS) buffers.delete(buffers.keys().next().value!);
  try {
    return await pending;
  } catch (error) {
    if (buffers.get(url) === pending) buffers.delete(url);
    throw error;
  }
}

export async function prepareRecordings(texts: string[]): Promise<void> {
  await Promise.all(texts.filter((text) => !!RECORDINGS[text]).map(bufferFor));
}

export async function playRecording(text: string, rate = 1, signal?: AbortSignal): Promise<void> {
  const buffer = await bufferFor(text);
  signal?.throwIfAborted();
  const ctx = audioContext();
  if (ctx.state !== 'running') throw new Error('audio_locked');
  await new Promise<void>((resolve, reject) => {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(ctx.destination);
    const abort = () => {
      source.stop();
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timeout = window.setTimeout(
      () => {
        source.stop();
        cleanup();
        reject(new Error('audio_interrupted'));
      },
      (buffer.duration / rate) * 1000 + 10000,
    );
    const cleanup = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
      source.disconnect();
      source.onended = null;
    };
    source.onended = () => {
      cleanup();
      resolve();
    };
    signal?.addEventListener('abort', abort, { once: true });
    source.start();
  });
}

export function delay(ms: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', abort, { once: true });
  });
}
